import { useCallback, useMemo } from "react";
import { Alert, Checkbox, Stack } from "@mantine/core";
import { useAuth } from "../../hooks/useAuth";
import {
  useSetSlideInsufficientTissue,
  useSlideTissueRows,
} from "../../hooks/useSlideInsufficientTissue";
import {
  slideTissueError,
  SlideTissueSource,
} from "../../services/slideInsufficientTissue.service";

interface Props {
  caseId: string;
  slideId: string;
  name: string;
  source: SlideTissueSource;
  disabled?: boolean;
}

export function SlideInsufficientTissueCheckbox({
  caseId,
  slideId,
  name,
  source,
  disabled,
}: Props) {
  const { user } = useAuth();
  const query = useSlideTissueRows(source, caseId);
  const mutation = useSetSlideInsufficientTissue();
  const slide = useMemo(
    () => query.data?.find((row) => row.id === slideId),
    [query.data, slideId]
  );
  const allowed = useMemo(
    () =>
      user?.permissions?.includes(
        `${source === "pathologist_review" ? source : "distribution"}.write`
      ),
    [user, source]
  );
  const error = useMemo(
    () =>
      mutation.error
        ? slideTissueError(mutation.error)
        : query.error
        ? "Slide flags could not be loaded. Reopen the case to try again."
        : undefined,
    [mutation.error, query.error]
  );
  const isDisabled = useMemo(
    () =>
      Boolean(
        disabled ||
          !allowed ||
          !slide ||
          mutation.isPending ||
          query.isError ||
          (source === "pathologist_review" &&
            (slide.canChange !== true || query.isLoading || query.isFetching))
      ),
    [
      disabled,
      allowed,
      slide,
      mutation.isPending,
      query.isError,
      source,
      query.isLoading,
      query.isFetching,
    ]
  );
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (isDisabled) return;
      mutation.mutate({
        caseId,
        source,
        slideId,
        insufficientTissue: event.currentTarget.checked,
      });
    },
    [caseId, source, slideId, mutation, isDisabled]
  );

  return (
    <Stack gap="xs">
      <Checkbox
        aria-label={`${name}: Insufficient tissue`}
        checked={slide?.insufficientTissue === true}
        onChange={handleChange}
        disabled={isDisabled}
      />
      {error && (
        <Alert
          color="red"
          variant="light"
          title="Could not update slide flag"
          role="alert"
        >
          {error}
        </Alert>
      )}
    </Stack>
  );
}
